import React, { useEffect, useState, useCallback, useRef } from "react";
import { MessageCircle, Copy, Check, ArrowRight, ChevronRight, Sparkles } from "lucide-react";
import { whatsappLink, whatsappStatus, whatsappInvite } from "../lib/api";
import { GoogleDots } from "./GoogleBits";
import { Button } from "./ui/button";
import { toast } from "sonner";

export default function WhatsAppConnect({ profile, onSkip }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const payload = {
        company: profile?.company || {},
        theme: {
          primary: profile?.colors?.primary,
          font: profile?.font,
          cover: profile?.doc?.cover ?? false,
          coverTitle: profile?.doc?.coverTitle || "Proposta Comercial",
          headerLabel: profile?.doc?.headerLabel || "Orçamento",
          logoRight: profile?.doc?.logoRight ?? false,
          logoSize: profile?.doc?.logoSize || "md",
        },
        layout: profile?.layout,
        logo: profile?.logo || null,
      };
      const res = await whatsappLink(payload);
      setInfo(res);
    } catch {
      toast.error("Não foi possível gerar o código agora.");
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    if (loadedRef.current) return; // run once (avoid StrictMode/double POST)
    loadedRef.current = true;
    load();
  }, [load]);

  // poll status until linked
  useEffect(() => {
    if (!info || info.linked) return;
    const t = setInterval(async () => {
      try {
        const s = await whatsappStatus();
        setInfo((prev) => ({ ...prev, ...s }));
        if (s.linked) {
          clearInterval(t);
          toast.success("WhatsApp conectado!");
        }
      } catch {}
    }, 4000);
    return () => clearInterval(t);
  }, [info]);

  const copyCode = async () => {
    if (!info?.link_code) return;
    try {
      await navigator.clipboard.writeText(info.link_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Código copiado");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  const number = info?.whatsapp_number || "";
  const joinKeyword = info?.join_keyword || "";
  const digits = number.replace(/[^\d]/g, "");
  const waHref = digits
    ? `https://wa.me/${digits}?text=${encodeURIComponent(joinKeyword || info?.link_code || "")}`
    : null;

  return (
    <div className="min-h-screen pt-14 flex flex-col items-center px-5">
      <div className="w-full max-w-xl pt-14 pb-24 float-up">
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <div className="grid place-items-center w-16 h-16 rounded-2xl bg-[#25D366]/10 ring-1 ring-[#25D366]/20">
              <MessageCircle size={30} className="text-[#128C7E]" strokeWidth={2} />
            </div>
            <span className="absolute -top-1.5 -right-1.5 grid place-items-center w-6 h-6 rounded-full google-gradient text-white shadow">
              <Sparkles size={12} strokeWidth={2.5} />
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-7 leading-tight">
            Leve a IA para o seu WhatsApp
          </h1>
          <p className="text-slate-500 mt-4 max-w-md text-[15px] leading-relaxed">
            Mande pelo WhatsApp o que quer orçar e receba o PDF pronto — com o seu modelo, logo e cores. Peça ajustes e ele reenvia na hora.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><GoogleDots /></div>
        ) : (
          <div className="mt-10 bg-white ring-1 ring-slate-200 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_-25px_rgba(15,23,42,0.15)]">
            {/* status */}
            <div className="flex items-center justify-center mb-7">
              {info?.linked ? (
                <span data-testid="wa-status-linked" className="inline-flex items-center gap-2 text-[13px] font-medium text-emerald-600 bg-emerald-50 rounded-full px-3.5 py-1.5">
                  <Check size={15} strokeWidth={3} /> Conectado {info.linked_phone ? `· +${info.linked_phone.replace(/^\+/, "")}` : ""}
                </span>
              ) : (
                <span data-testid="wa-status-waiting" className="inline-flex items-center gap-2.5 text-[13px] font-medium text-slate-500 bg-slate-50 rounded-full px-3.5 py-1.5">
                  <GoogleDots /> Aguardando conexão…
                </span>
              )}
            </div>

            {info?.linked ? (
              <div className="text-center py-2">
                <div className="text-[15px] font-semibold text-slate-900">Tudo pronto! 🎉</div>
                <p className="text-[13px] text-slate-500 mt-2 max-w-sm mx-auto">
                  Seu WhatsApp está conectado{info.linked_company ? <> à empresa <span className="font-semibold text-slate-700" data-testid="wa-linked-company">{info.linked_company}</span></> : " ao modelo"}. Abra a conversa e mande o que quer orçar — o PDF chega prontinho. Envie <span className="font-mono-jb text-slate-700">novo</span> para começar outro ou <span className="font-mono-jb text-slate-700">empresa</span> para trocar de empresa.
                </p>
                {waHref && (
                  <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="wa-open-btn" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white bg-[#25D366] hover:bg-[#1fbe5a] rounded-full px-4 py-2 transition-colors mt-4">
                    <MessageCircle size={15} /> Abrir conversa <ArrowRight size={14} />
                  </a>
                )}
              </div>
            ) : (
              <>
                <Step n={1} title="Abra o chat do assistente no WhatsApp">
                  {info?.configured ? (
                    <div className="mt-2 space-y-2">
                      <p className="text-[13px] text-slate-500">
                        {joinKeyword ? (
                          <>Envie <span className="font-mono-jb text-slate-800 bg-slate-100 rounded px-1.5 py-0.5">{joinKeyword}</span> para o número <span className="font-mono-jb text-slate-800">+{digits}</span>.</>
                        ) : (
                          <>Abra a conversa com o número <span className="font-mono-jb text-slate-800">+{digits}</span> (Basiq). Pelo botão abaixo, a mensagem já vem com seu código.</>
                        )}
                      </p>
                      {waHref && (
                        <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="wa-open-btn" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white bg-[#25D366] hover:bg-[#1fbe5a] rounded-full px-4 py-2 transition-colors">
                          <MessageCircle size={15} /> Abrir no WhatsApp <ArrowRight size={14} />
                        </a>
                      )}
                      <InviteForm />
                    </div>
                  ) : (
                    <p className="mt-2 text-[13px] text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                      A conexão com o WhatsApp ainda está sendo configurada. Guarde seu código abaixo — assim que estiver ativa, é só enviá-lo.
                    </p>
                  )}
                </Step>

                <div className="h-px bg-slate-100 my-5" />

                <Step n={2} title="Envie seu código de vinculação">
                  <p className="text-[13px] text-slate-500 mt-1">Isso conecta o bot ao seu modelo de orçamento.</p>
                  <button
                    onClick={copyCode}
                    data-testid="wa-link-code"
                    className="group mt-3 w-full flex items-center justify-between bg-slate-50 hover:bg-slate-100 rounded-2xl px-5 py-4 transition-colors"
                  >
                    <span className="font-mono-jb text-2xl tracking-[0.35em] text-slate-900">{info?.link_code}</span>
                    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 group-hover:text-slate-800">
                      {copied ? <><Check size={14} className="text-emerald-500" /> Copiado</> : <><Copy size={14} /> Copiar</>}
                    </span>
                  </button>
                </Step>

                <div className="h-px bg-slate-100 my-5" />

                <Step n={3} title="Peça seu orçamento" last>
                  <p className="text-[13px] text-slate-500 mt-1">
                    Ex.: <span className="text-slate-700">“Orçamento de logo para o cliente João por R$ 900, entrega em 10 dias.”</span> O assistente responde com o PDF pronto. Envie <span className="font-mono-jb text-slate-700">novo</span> para começar outro.
                  </p>
                </Step>
              </>
            )}
          </div>
        )}

        <div className="flex justify-center mt-8">
          <Button variant="ghost" onClick={onSkip} data-testid="wa-skip-btn" className="rounded-full text-slate-500 hover:text-slate-900">
            Ir para o app <ChevronRight size={16} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

const Step = ({ n, title, children, last }) => (
  <div className="flex gap-4">
    <div className="shrink-0 grid place-items-center w-7 h-7 rounded-full google-gradient text-white text-[13px] font-bold">{n}</div>
    <div className={`flex-1 ${last ? "" : ""}`}>
      <div className="text-[15px] font-semibold text-slate-900">{title}</div>
      {children}
    </div>
  </div>
);

const InviteForm = () => {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await whatsappInvite(phone);
      setSent(r.to);
      toast.success(`Convite enviado para ${r.to}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && (d.message || (typeof d === "string" ? d : null))) || "Não foi possível enviar o convite.");
    } finally {
      setBusy(false);
    }
  };
  if (sent) return <p className="mt-3 text-[12.5px] text-emerald-700" data-testid="wa-invite-sent">Mensagem enviada para {sent}. Responda com o código para conectar.</p>;
  return (
    <form onSubmit={submit} className="mt-4 pt-4 border-t border-slate-100" data-testid="wa-invite-form">
      <p className="text-[12px] text-slate-400 mb-2">Ou receba uma mensagem nossa com o código:</p>
      <div className="flex gap-2">
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(49) 99955-2743" inputMode="tel" data-testid="wa-invite-phone"
          className="flex-1 h-9 rounded-full ring-1 ring-slate-200 focus:ring-2 focus:ring-slate-900 outline-none px-3.5 text-[13px] text-slate-800 placeholder:text-slate-400 transition-all" />
        <button type="submit" disabled={busy || phone.replace(/\D/g, "").length < 10} data-testid="wa-invite-btn" className="h-9 px-4 rounded-full bg-slate-900 text-white text-[12.5px] font-semibold hover:bg-slate-800 disabled:opacity-40 transition-colors">
          {busy ? "Enviando…" : "Enviar"}
        </button>
      </div>
    </form>
  );
};
